import React from 'react';
import PropTypes from 'prop-types';
import dynamic from 'next/dynamic';
import DOMPurify from 'isomorphic-dompurify';
import Media from '../../Generic/Carousel/CarouselMedia';
import { product } from '../../../constants/messages/pt-BR';

const Typography = dynamic(() => import('../../Generic/Typography'));

/**
 * BambuBanner Component
 * Unified component for rendering Bambu-style product banners (firstBanner, secondBanner, middleBanner, fourthBanner, bottomBanner)
 * 
 * Data Flow:
 * - bannerData: Object containing banner content (title, bannerTitle, disclaimerText, etc.)
 * - bannerType: String indicating banner template type ('topBanner', 'simpleBanner', 'middleBanner', 'bottomBanner', 'ratingReviewBanner')
 * - bannerImage: Nested object with image URLs for desktop and smartphone
 * 
 * @component
 */
const BambuPageBanner = ({ bannerData, bannerType = 'topBanner' }) => {
  // Guard against missing data
  if (!bannerData) {
    return null;
  }

  // Check if banner should render based on conditions
  const shouldRender = () => {
    const bannerImageUrl = bannerData?.bannerImage?.image?.desktopImage?.url;
    const bannerStyle = bannerData?.styles;

    if (bannerType === 'topBanner') {
      return bannerStyle === 'bambupdpfirstbanner' && bannerImageUrl;
    } else if (bannerType === 'simpleBanner') {
      return bannerStyle === 'bambupdpsecondbanner' && bannerImageUrl;
    } else if (bannerType === 'middleBanner') {
      return bannerStyle === 'bambupdpthirdbanner' && bannerImageUrl;
    } else if (bannerType === 'bottomBanner') {
      return bannerStyle === 'bambupdpfourthbanner' && bannerImageUrl;
    } else if (bannerType === 'ratingReviewBanner') {
      return bannerStyle === 'bambupdpfifthbanner' && bannerImageUrl;
    }
    return false;
  };

  if (!shouldRender()) {
    return null;
  }

  // Render banner image - shared across all banner types
  const renderBannerImage = () => (
    <Media
      component="bannerPicture"
      srcset={bannerData?.bannerImage?.image?.desktopImage?.url}
      image={bannerData?.bannerImage?.image?.smartphoneImage?.url}
      altText={bannerData?.bannerImage?.image?.altText}
      className="w-full section-banner-img object-cover object-center"
    />
  );

  // Top Banner (bambupdpfirstbanner)
  const renderTopBanner = () => (
    <div className={`min-h-min bambupdpfirstbanner bambupdpBanners mb-10 component-top-banner relative clear-both pdp-banner ${bannerData.styles}`}>
      {renderBannerImage()}      
      <div className="absolute bambupdpfirstbannerWrap bambupdpBannerswrap w-full md:w-50p flex flex-col justify-start md:justify-center px-30 md:px-40 lg:pb-14 md:right-30 md:right-0 top-[10%] md:top-[28%] left-[3%]" tabIndex={0}>
        {/* Display bannerTitle */}
        {bannerData?.bannerTitle && (
          <Typography
            className="banner-title-main mt-30 font-stemLight tracking-0 font-400 m-0 text-22 md:text-42 uppercase text-black md:text-black text-left md:text-left"
            component="h2"
            dangerouslySetInnerHTML={{
              __html: DOMPurify.sanitize(bannerData?.bannerTitle),
            }}
          tabIndex={0}/>
        )}


        <hr className="bambu-gradient-hr mt-15 md:mt-20 mb-15 md:mb-20 w-80p" />

        {/* Display topText with percentage */}
        {bannerData?.topText && (
          <div className="top-title">
            <Typography
              component="p"
              className="font-stemLight font-400 tracking-0 text-18 md:text-30 w-full md:w-90p text-black"
            tabIndex={0}>
              {bannerData?.topText}
            </Typography>

          </div>
        )}

        {/* Disclaimer text */}
        {bannerData?.disclaimerText && (
          <Typography
            className="disclaimer_text text-black tracking-0 mt-15 md:mt-20 text-10 md:text-14 font-stemLight md:pl-0 md:mx-auto inline-block w-full text-left"
            component="div"
            tabIndex={0}
            dangerouslySetInnerHTML={{
              __html: DOMPurify.sanitize(bannerData?.disclaimerText),
            }}
          />
        )}
      </div>
    </div>
  );

  // Simple Banner (bambupdpsecondbanner)
  const renderSimpleBanner = () => (
    <div className={`min-h-min bambupdpsecondbanner bambupdpBanners mb-10 component-top-banner relative clear-both pdp-banner ${bannerData?.styles}`}>
      {renderBannerImage()}
      <div className="absolute bambupdpsecondbannerWrap bambupdpBannerswrap w-full md:w-60p right-0 float-right flex flex-col justify-start md:justify-center pr-30 md:pr-20 top-40 md:top-80" tabIndex={0}>
        {/* Display bannerTitle */}
        {bannerData?.bannerTitle && (
          // <div className="banner-title mb-10">
          <Typography
            className="banner-title-main font-stemLight tracking-0 md:font-GigaSansBold font-700 md:font-700 text-24 md:text-[42px] uppercase text-black md:text-black text-right md:text-right"
            component="h2"
            dangerouslySetInnerHTML={{
              __html: DOMPurify.sanitize(bannerData?.bannerTitle || ''),
            }}
          tabIndex={0}/>
          // </div>
        )}


        {/* Display topText with percentage */}
        {bannerData?.topText && (
          <div className="top-title">
            <Typography
              component="p"
              className="text-16 md:text-28 mt-25 md:mt-20 tracking-0 text-right text-black font-stemLight font-400 md:GigaSansMedium md:font-500 uppercase"
              dangerouslySetInnerHTML={{
              __html: DOMPurify.sanitize(bannerData?.topText),
            }}
            tabIndex={0}/>

          </div>
        )}

        {/* Display disclaimerText */}
        {bannerData?.disclaimerText && (
          <Typography
            className="text-black disclaimer_text mt-25 md:mt-20 tracking-0 text-10 md:text-15 lg:text-15 font-stemLight md:font-GigaSansRegular font-400 text-right"
            component="div"
            dangerouslySetInnerHTML={{
              __html: DOMPurify.sanitize(bannerData?.disclaimerText),
            }}
          tabIndex={0}/>
        )}

      </div>
    </div>
  );

  // middle Banner (bambupdpthirdbanner)
  const renderMiddleBanner = () => (
    <div className={`min-h-min bambupdpthirdbanner bambupdpBanners mb-10 component-top-banner relative clear-both pdp-banner ${bannerData?.styles}`}>
      {renderBannerImage()}
      <div className="absolute bambupdpthirdbannerWrap bambupdpBannerswrap w-full md:w-52p flex flex-col justify-start md:justify-center px-30 left-0 md:left-10 right-10 md:right-0 top-40 md:top-60" tabIndex={0}>
        {/* Display bannerTitle */}
        {bannerData?.bannerTitle && (
          <div className="banner-title">
            <Typography
              className="banner-title-main mt-10 font-stemLight tracking-0 font-700 m-0 border-0 text-24 md:text-[42px] uppercase text-black md:text-black text-left md:text-left"
              component="h2"
              dangerouslySetInnerHTML={{
                __html: DOMPurify.sanitize(bannerData?.bannerTitle || ''),
              }}
            tabIndex={0}/>
          </div>
        )}
        {/* Display topText with percentage */}
        {bannerData?.topText && (
          <div className="top-title w-100p md:w-90p">
            <Typography
              component="p"
              className="text-14 md:text-24 tracking-0 pt-25 md:pt-20 md:font-medium text-black font-GigaSansMedium uppercase"
              dangerouslySetInnerHTML={{
                __html: DOMPurify.sanitize(bannerData?.topText || ''),
              }}
            tabIndex={0}/>

          </div>
        )}

      </div>
    </div>
  );

  // Bottom Banner (bambupdpfourthbanner)
  const renderBottomBanner = () => {
    // Process bannerTitle to add line break after "Recomendado para" for desktop
    const processedBannerTitle = bannerData?.bannerTitle 
      ? bannerData.bannerTitle.replace(/(Recomendado para)\s+/i, '$1 <br class="hidden md:block"/>')
      : '';

    return (
    <div className={`min-h-min bambupdpfourthbanner bambupdpBanners mb-10 component-top-banner relative clear-both pdp-banner ${bannerData.styles}`}>
      {renderBannerImage()}

      <div className="absolute bambupdpfourthbannerWrap bambupdpBannerswrap w-full md:w-53p flex flex-col justify-start md:justify-center left-0 md:left-30 right-30 md:right-0 pl-20 pr-30 md:pr-40 top-[30px] md:top-100 text-right md:text-left" tabIndex={0}>
        <div className="w-full">
          {/* Display bannerTitle */}
          {bannerData?.bannerTitle && (
            <Typography
              className="banner-title-main font-GigaSansBold tracking-0 font-bold text-22 md:text-[38px] uppercase text-black md:text-black w-100p md:w-100p text-right md:text-left"
              component="h2"
              dangerouslySetInnerHTML={{
                __html: DOMPurify.sanitize(processedBannerTitle)
              }}
            tabIndex={0}/>
          )}

          <hr className="bambu-gradient-hr hidden md:block mt-10 mb-10 md:mt-30 md:mb-30 w-80p" />

          {/* Display topText with percentage */}
          {bannerData?.topText && (
            <div className="top-title">
              <Typography
                component="p"
                className="text-15 md:text-26 tracking-0 font-20 w-full md:w-95p mt-15 md:mt-0 text-black font-GigaSansRegular uppercase"
                dangerouslySetInnerHTML={{
                  __html: DOMPurify.sanitize(bannerData?.topText)
                }}
              tabIndex={0}/>

              <div className="flex mt-15 md:mt-20 bambubannerLinkwrap">
                {bannerData?.percentageText && (
                  <Typography
                    component="a"
                    href="#"
                    textdecoration="underline"
                    className="bambubannerLink text-13 md:text-20 tracking-0 font-GigaSansRegular uppercase"
                    dangerouslySetInnerHTML={{
                      __html: DOMPurify.sanitize(bannerData?.percentageText || ''),
                    }}
                  tabIndex={0}/>
                )}
              </div>

            </div>
          )}

          {/* Disclaimer text */}
          {bannerData?.disclaimerText && (
            <Typography
              className="disclaimer_text text-black mt-10 md:mt-20 tracking-0 text-8 md:text-10 font-GigaSansRegular"
              component="div"
              dangerouslySetInnerHTML={{
                __html: DOMPurify.sanitize(bannerData?.disclaimerText)
              }}
            tabIndex={0}/>
          )}
        </div>
      </div>
    </div>
    );
  };

  // Rating Review Banner (bambupdpfithbanner)
  const renderRatingReviewBanner = () => (
    <div className={`min-h-min bambupdpfifthbanner bambupdpBanners mb-10 component-top-banner relative clear-both pdp-banner ${bannerData.styles}`}>
      {renderBannerImage()}
      <div className="absolute bambupdpfifthbannerWrap bambupdpBannerswrap w-full md:w-55p right-0 float-left md:float-right text-left md:text-right flex flex-col justify-start md:justify-center px-30 md:px-70 top-[30px] md:top-60" tabIndex={0}>
        <div className="">
          {/* Display bannerTitle */}
          {bannerData?.bannerTitle && (
            <Typography
              className="banner-title-main font-GigaSansBold tracking-0 text-20 md:text-[40px] uppercase text-black md:text-black uppercase"
              component="h2"
              dangerouslySetInnerHTML={{
                __html: DOMPurify.sanitize(bannerData?.bannerTitle)
              }}
            tabIndex={0}/>
          )}

          <hr className="bambu-gradient-hr mt-10 text-right md:mt-15 mb-10 md:mb-15" />

          {/* Display topText with percentage */}
          {bannerData?.topText && (
            <div className="top-title">
              <Typography
                component="p"
                className="text-15 md:text-30 tracking-0 text-black font-GigaSansRegular mb-10 md:mb-15 uppercase"
                dangerouslySetInnerHTML={{
                  __html: DOMPurify.sanitize(bannerData?.topText)
                }}
              tabIndex={0}/>


             {/* <div className="flex mt-10"> */}
                {bannerData?.percentageText && (
                  <Typography
                    component="p"
                    className="bambubannerLink text-15 md:text-26 tracking-0 font-GigaSansExtraBold mb-15 md:mb-30 uppercase"
                    dangerouslySetInnerHTML={{
                      __html: DOMPurify.sanitize(bannerData?.percentageText || ''),
                    }}
                  tabIndex={0}/>
                )}
              {/* </div> */}


            </div>
          )}


          {/* All list START  */}
          {bannerData && bannerData?.productListCollection && bannerData?.productListCollection?.items?.length > 0 && (
            <div className="bambuProductListCollection w-auto mt-20 mx-auto">
              {bannerData.productListCollection.items.map((item, index) => {
              return (
              <div key={index} className="numberCountParent mt30 mb20 flex justify-start md:justify-end font-semibold text-black">
                <div className="borderBannerWhite">
                  <span className="numberCount" tabIndex={0}>{index + 1}</span>
                  <div className="banneritemText">
                    {item?.productName && (
                      <Typography
                        component="p"
                        className="text-8 md:text-10 tracking-0 font-GigaSansRegular font-400"
                        tabIndex={0}
                        dangerouslySetInnerHTML={{
                          __html: DOMPurify.sanitize(item.productName)
                        }}
                      />
                    )}
                    {item?.category && (
                      <Typography
                        component="h3"
                        className="text-18 md:text-22 tracking-0 text-black font-GigaSansBold mb-5 md:mb-10"
                        dangerouslySetInnerHTML={{
                          __html: DOMPurify.sanitize(item.category)
                        }}
                      tabIndex={0} />
                    )}
                  </div>
                </div>
              </div>
            );
            })}</div>
          )
          }


          {/* All list END  */}
        </div>
      </div>
    </div>
  );

  // Render based on banner type
  const renderSwitch = () => {
    switch (bannerType) {
      case 'topBanner':
        return renderTopBanner();
      case 'simpleBanner':
        return renderSimpleBanner();
      case 'middleBanner':
        return renderMiddleBanner();
      case 'bottomBanner':
        return renderBottomBanner();
      case 'ratingReviewBanner':
        return renderRatingReviewBanner();
      default:
        return null;
    }
  };

  return renderSwitch();
};

BambuPageBanner.propTypes = {
  /** Banner data object containing all content and metadata */
  bannerData: PropTypes.shape({
    styles: PropTypes.string,
    bannerImage: PropTypes.shape({
      image: PropTypes.shape({
        desktopImage: PropTypes.shape({
          url: PropTypes.string,
        }),
        smartphoneImage: PropTypes.shape({
          url: PropTypes.string,
        }),
        altText: PropTypes.string,
      }),
    }),
    title: PropTypes.string,
    bannerTitle: PropTypes.string,
    disclaimerText: PropTypes.string,
    topText: PropTypes.string,
    percentageText: PropTypes.string,
    discoverNewText: PropTypes.string,
    hairTypeLevel1: PropTypes.string,
    // productListCollection: PropTypes.shape({
    //   items: PropTypes.arrayOf(
    //     PropTypes.shape({
    //         productName: PropTypes.string, 
    //         category: PropTypes.string, 
    //         styles: PropTypes.string })),
    // }),
  }),
  /** Banner template type: 'topBanner' | 'simpleBanner' | 'middleBanner' | 'bottomBanner' | 'ratingReviewBanner' */
  bannerType: PropTypes.oneOf(['topBanner', 'simpleBanner', 'middleBanner', 'bottomBanner', 'ratingReviewBanner']),
};

BambuPageBanner.defaultProps = {
  bannerData: null,
  bannerType: 'topBanner',
};

export default BambuPageBanner;